import { useMemo, useState } from 'react';
import { AdminClubsScores, AdminTourAveragesUnits, ClubKey, clubs, NewTourParameterData, TourAverageParameterOption, TourAveragesData, TourAveragesParameter } from 'types/admin/tourAverages';

const cleanTourParameterData: NewTourParameterData = {
  parameterId: '',
  unit: '',
};

const createEmptyClubScores = (): AdminClubsScores =>
  Object.fromEntries(clubs.map((club) => [club.key, null])) as AdminClubsScores;

type UseTourAveragesEditorParams = {
  initialData: TourAveragesData;
  parameterOptions: TourAverageParameterOption[];
};

export const useTourAveragesEditor = ({ initialData, parameterOptions }: UseTourAveragesEditorParams) => {
  const [tourAveragesData, setTourAveragesData] = useState(initialData);
  const [newTourParameterData, setNewTourParameterData] = useState<NewTourParameterData>(cleanTourParameterData);

  const availableParameters = useMemo(
    () => parameterOptions.filter((parameter) => !tourAveragesData.pga.some((pgaParameter) => pgaParameter.parameterId === parameter.id)),
    [parameterOptions, tourAveragesData.pga]
  );

  const handleDeleteColumn = (index: number) => {
    setTourAveragesData((current) => ({
      ...current,
      pga: current.pga.filter((_, pgaIndex) => pgaIndex !== index),
      lpga: current.lpga.filter((_, lpgaIndex) => lpgaIndex !== index),
    }));
  };

  const handleYearChange = (year: number) => {
    setTourAveragesData((current) => ({ ...current, year }));
  };

  const handleNewParameterSelected = (parameterId: string) => {
    setNewTourParameterData(parameterId === '' ? cleanTourParameterData : { ...cleanTourParameterData, parameterId });
  };

  const handleNewParameterUnitChanged = (unit: AdminTourAveragesUnits) => {
    setNewTourParameterData((current) => (current.parameterId === '' ? current : { ...current, unit }));
  };

  const handleAddParameter = () => {
    if (newTourParameterData.parameterId === '') return;

    const parameterName = parameterOptions.find((option) => option.id === newTourParameterData.parameterId)?.title ?? '';
    const newParameter = { ...newTourParameterData, parameterName };

    setTourAveragesData((current) => ({
      ...current,
      pga: [...current.pga, { ...newParameter, clubs: createEmptyClubScores() }],
      lpga: [...current.lpga, { ...newParameter, clubs: createEmptyClubScores() }],
    }));
    setNewTourParameterData(cleanTourParameterData);
  };

  const handleScoreChange = (kind: 'PGA' | 'LPGA', columnIndex: number, club: ClubKey, value: string) => {
    const updateParameters = (parameters: TourAveragesParameter[]) =>
      parameters.map((parameter, index) => (index === columnIndex ? { ...parameter, clubs: { ...parameter.clubs, [club]: value } } : parameter));

    setTourAveragesData((current) =>
      kind === 'PGA'
        ? {
            ...current,
            pga: updateParameters(current.pga),
          }
        : {
            ...current,
            lpga: updateParameters(current.lpga),
          }
    );
  };

  return {
    tourAveragesData,
    newTourParameterData,
    availableParameters,
    handleDeleteColumn,
    handleYearChange,
    handleNewParameterSelected,
    handleNewParameterUnitChanged,
    handleAddParameter,
    handleScoreChange,
  };
};
